import React from 'react';
import { useContext, useEffect, useCallback } from "react";
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import { GridSize } from '@material-ui/core'
import { LanguageContext, EfileOverride } from "../Helpers/Context";
import useAxios from 'axios-hooks';

const useStyles = makeStyles((theme) => ({
    left: {
        textAlign: 'left'
    },
    right: {
        textAlign: 'right'
    }
}));

export default function EfileSettings() {
    const { dictionary, officeId } = useContext(LanguageContext);
    const [hasChange, setHasChange] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [saveError, setSaveError] = React.useState(false);

    const col1: GridSize = 3;
    const col2: GridSize = 2;
    const col3: GridSize = 3;
    const col4: GridSize = 4;

    const classes = useStyles();

    const [newOverride, setNewOverride] = React.useState<EfileOverride>({
        officeId: officeId,
        craEfileAgentNumber: "",
        craPassword: "",
        mrqAgentNumber: "",
        mrqBusinessNumber: "",
        mrqPassword: "",
        discounterCode: 0,
        optionType: "",
        discounterPhoneNumber: "",
        discounterAddress1: "",
        discounterAddress2: "",
        discounterCity: "",
        discounterProvince: "",
        discounterPostalCode: ""
    });

    const [{ data, loading }, refetch] = useAxios<EfileOverride>(
        `Efile/${officeId}`
    );

    const [, executePut] = useAxios(
        {
            url: `Efile/${officeId}`,
            data: newOverride,
            method: "PUT"
        },
        { manual: true }
    );

    const handleSavedClose = () => {
        setSaved(false);
    };

    const handleErrorClose = () => {
        setSaveError(false);
    };

    const handleSelectChange = (event: React.ChangeEvent<{ name?: string | undefined, value: unknown }>) => {
        if(!event.target.name) return;
        setHasChange(true);
        setNewOverride({ ...newOverride, [event.target.name]: event.target.value });
    };
      
    const handleStringChange = (event: React.ChangeEvent<{ name: string, value: string }>) => {
        setHasChange(true);
        setNewOverride({ ...newOverride, [event.target.name]: event.target.value });
    };

    const handleNumberChange = (event: React.ChangeEvent<{ name: string, value: string }>) => {
        let newValue = parseFloat(event.target.value);
        setHasChange(true);
        let truncValue = Math.round(newValue);
        setNewOverride({ ...newOverride, [event.target.name]: truncValue });
    };

    const cancelChange = () => {
        setInitialValues();
    };

    async function saveChange() {
        if (newOverride.craPassword.trim() === '' || newOverride.mrqPassword.trim() === '') {
            setSaveError(true);
        }
        else if (hasChange) {
            await executePut();
            await refetch();
            setHasChange(false);
            setSaved(true);
        }
    };

    const setInitialValues = useCallback(() => {
        let initOverride: EfileOverride =
        {
            officeId: officeId,
            craEfileAgentNumber: "",
            craPassword: "",
            mrqAgentNumber: "",
            mrqBusinessNumber: "",
            mrqPassword: "",
            discounterCode: 0,
            optionType: "",
            discounterPhoneNumber: "",
            discounterAddress1: "",
            discounterAddress2: "",
            discounterCity: "",
            discounterProvince: "",
            discounterPostalCode: ""
        }

        if (data)
        {
            initOverride.craEfileAgentNumber = data.craEfileAgentNumber ?? "";
            initOverride.mrqAgentNumber = data.mrqAgentNumber ?? "";
            initOverride.mrqBusinessNumber = data.mrqBusinessNumber ?? "";
            initOverride.discounterCode = data.discounterCode ?? 0;
            initOverride.discounterPhoneNumber = data.discounterPhoneNumber ?? "";
            initOverride.discounterAddress1 = data.discounterAddress1 ?? "";
            initOverride.discounterAddress2 = data.discounterAddress2 ?? "";
            initOverride.discounterCity = data.discounterCity ?? "";
            initOverride.discounterProvince = data.discounterProvince ?? "";
            initOverride.discounterPostalCode = data.discounterPostalCode ?? "";
            initOverride.optionType = data.optionType ?? "";
        }

        setNewOverride(initOverride);
        setHasChange(false);
    }, [officeId, data]);

    useEffect(() => {
        // Update override when officeId changes
        if (data && !loading) {
            setInitialValues();
            setHasChange(false);
        }
    }, [data, loading, officeId, setInitialValues]);


    
    return (
        <main style={{ padding: "1rem" }}>
            <Typography variant="h6" color="primary">
                {dictionary.efileTitle}
            </Typography>
            <Typography variant="subtitle1" color="primary" style={{ padding: "1rem" }}>
                {dictionary.efileSubtitle}
            </Typography>
            {loading && (
                <CircularProgress />
            )}
            {data && !loading && (
                <FormControl>
                    <Grid container spacing={1} direction="row" justifyContent="center" alignItems="flex-start">

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary" >
                                {dictionary.office}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                InputProps={{ disableUnderline: true }}
                                value={newOverride.officeId} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.craEfileAgentNumber}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 32 }}
                                onChange={handleStringChange}
                                name="craEfileAgentNumber"
                                value={newOverride.craEfileAgentNumber} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.craPassword}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 32 }}
                                onChange={handleStringChange}
                                placeholder={dictionary.craPasswordHint}
                                name="craPassword"
                                value={newOverride.craPassword} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.discounterCode}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                type="number"
                                onChange={handleNumberChange}
                                name="discounterCode"
                                value={newOverride.discounterCode} />
                        </Grid>
                        <Grid item xs={col4} />

                        { newOverride.optionType === 'A' && (
                            <React.Fragment>
                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.discounterPhoneNumber}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 14 }}
                                onChange={handleStringChange}
                                name="discounterPhoneNumber"
                                value={newOverride.discounterPhoneNumber} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.discounterAddress1}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 80 }}
                                onChange={handleStringChange}
                                placeholder={dictionary.discounterAddress1Hint}
                                name="discounterAddress1"
                                value={newOverride.discounterAddress1} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.discounterAddress2}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 80 }}
                                onChange={handleStringChange}
                                name="discounterAddress2"
                                placeholder={dictionary.discounterAddress2Hint}
                                value={newOverride.discounterAddress2} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.discounterCity}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 50 }}
                                onChange={handleStringChange}
                                name="discounterCity"
                                value={newOverride.discounterCity} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.discounterProvince}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} alignItems="flex-start">
                            <Select
                                style={{width: 150, textAlign: 'left', textIndent: 2, display: "flex"}}
                                onChange={handleSelectChange}
                                name="discounterProvince"
                                value={newOverride.discounterProvince}
                            >
                                <MenuItem></MenuItem>
                                <MenuItem value="AB">AB</MenuItem>
                                <MenuItem value="BC">BC</MenuItem>
                                <MenuItem value="MB">MB</MenuItem>
                                <MenuItem value="NB">NB</MenuItem>
                                <MenuItem value="NL">NL</MenuItem>
                                <MenuItem value="NS">NS</MenuItem>
                                <MenuItem value="NT">NT</MenuItem>
                                <MenuItem value="NU">NU</MenuItem>
                                <MenuItem value="ON">ON</MenuItem>
                                <MenuItem value="PE">PE</MenuItem>
                                <MenuItem value="QC">QC</MenuItem>
                                <MenuItem value="SK">SK</MenuItem>
                                <MenuItem value="YT">YT</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.discounterPostalCode}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 7 }}
                                onChange={handleStringChange}
                                name="discounterPostalCode"
                                value={newOverride.discounterPostalCode} />
                        </Grid>
                        <Grid item xs={col4} />
                        </React.Fragment>
                        )}

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.mrqBusinessNumber}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 32 }}
                                onChange={handleStringChange}
                                name="mrqBusinessNumber"
                                value={newOverride.mrqBusinessNumber} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.mrqAgentNumber}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 32 }}
                                onChange={handleStringChange}
                                name="mrqAgentNumber"
                                value={newOverride.mrqAgentNumber} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={col1} />
                        <Grid item xs={col2} className={classes.right}>
                            <Typography variant="subtitle2" display="inline" color="primary">
                                {dictionary.mrqPassword}:
                            </Typography>
                        </Grid>
                        <Grid item xs={col3} >
                            <TextField fullWidth
                                inputProps={{ maxLength: 32 }}
                                onChange={handleStringChange}
                                placeholder={dictionary.mrqPasswordHint}
                                name="mrqPassword"
                                value={newOverride.mrqPassword} />
                        </Grid>
                        <Grid item xs={col4} />

                        <Grid item xs={4} />
                        <Grid item xs={4} >
                            <Typography variant="subtitle1" color="primary" style={{ padding: "1rem" }} className={classes.left}>
                                {dictionary.efileWarning}
                            </Typography>
                        </Grid>
                        <Grid item xs={4} />

                        <Grid item xs={col1} />
                        <Grid item xs="auto" >
                            <Button variant="contained" color="primary" disabled={!hasChange} onClick={saveChange}>{dictionary.save}</Button>
                        </Grid>
                        <Grid item xs="auto" >
                            <Button variant="contained" color="primary" disabled={!hasChange} onClick={cancelChange}>{dictionary.cancel}</Button>
                        </Grid>
                    </Grid>
                    <Snackbar
                        open={saved}
                        autoHideDuration={4000}
                        onClose={handleSavedClose}
                        message={dictionary.changesSaved}
                    />
                    <Snackbar
                        open={saveError}
                        autoHideDuration={4000}
                        onClose={handleErrorClose}
                    >
                        <Chip
                            label={dictionary.passwordsRequired}
                            color="secondary"
                        />
                    </Snackbar>
                </FormControl>
            )}
        </main>
    );
}